/**
 * MTProto Connection History Timeline
 * Last 7 days of connect/disconnect/recovered events.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Activity, RefreshCw, Wifi, WifiOff, ArrowUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_META = {
  connected:    { label: 'Connected',    color: '#10B981', icon: Wifi,    bg: 'bg-[#10B981]/10' },
  disconnected: { label: 'Disconnected', color: '#E63946', icon: WifiOff, bg: 'bg-[#E63946]/10' },
  recovered:    { label: 'Recovered',    color: '#3B82F6', icon: ArrowUp, bg: 'bg-[#3B82F6]/10' },
};

const STATUS_META = {
  connected:    { label: 'Connected',    color: '#10B981', icon: Wifi },
  disconnected: { label: 'Disconnected', color: '#E63946', icon: WifiOff },
  disabled:     { label: 'Disabled',     color: '#6B7280', icon: WifiOff },
  unknown:      { label: 'Unknown',      color: '#6B7280', icon: Activity },
};

export default function MtprotoConnectionHistory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const token = localStorage.getItem('token');
  const h = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/telegram/mtproto/events?days=${days}`, { headers: h });
      setData(r.data);
    } catch (e) {
      toast.error('Failed to load MTProto events');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const health = data?.current_health || {};
  const sm = STATUS_META[health.status] || STATUS_META.unknown;
  const StatusIcon = sm.icon;

  // Group events by date
  const grouped = {};
  (data?.events || []).forEach(e => {
    const day = String(e.at).slice(0, 10);
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(e);
  });
  const sortedDays = Object.keys(grouped).sort().reverse();

  return (
    <div className="space-y-4" data-testid="mtproto-history">
      {/* Current status */}
      <div className={`rounded-xl p-4 border ${sm.color === '#10B981' ? 'bg-[#10B981]/5 border-[#10B981]/30' : sm.color === '#E63946' ? 'bg-[#E63946]/5 border-[#E63946]/30' : 'bg-[hsl(var(--surface))] border-[hsl(var(--border))]'}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${sm.color}20` }}>
              <StatusIcon size={18} style={{ color: sm.color }} />
            </div>
            <div>
              <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider">MTProto Status</p>
              <p className="text-white text-base font-semibold" style={{ color: sm.color }}>{sm.label}</p>
              <p className="text-[10px] text-[#6B7280] mt-0.5">
                {health.last_check_at && `Last check: ${new Date(health.last_check_at).toLocaleString('en-MY')}`}
                {health.consecutive_failures > 0 && (
                  <span className="text-[#E63946] ml-2">· {health.consecutive_failures} consecutive failures</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
              data-testid="mtproto-days-select"
              className="bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] text-white text-xs rounded-md px-2 py-1.5">
              <option value={1}>Last 1 day</option>
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <Button onClick={load} disabled={loading} size="sm" variant="ghost" className="text-[#9CA3AF] hover:text-white">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </div>
      </div>

      {/* Event timeline */}
      <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl p-3">
        <div className="flex items-center gap-2 px-2 py-1 mb-2">
          <Activity size={14} className="text-[#9CA3AF]" />
          <p className="text-white text-sm font-semibold">Connection Timeline</p>
          <span className="text-[10px] text-[#6B7280]">({data?.events?.length || 0} events in last {days} day{days === 1 ? '' : 's'})</span>
        </div>

        {sortedDays.length === 0 ? (
          <div className="px-3 py-8 text-center">
            <p className="text-[#9CA3AF] text-sm">No events in this period.</p>
            <p className="text-[10px] text-[#6B7280] mt-1">Connection is stable, or MTProto is disabled.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedDays.map(day => (
              <div key={day} className="border-l-2 border-[hsl(var(--border))] pl-3 ml-2">
                <p className="text-[10px] text-[#9CA3AF] uppercase tracking-wider mb-1.5">
                  {new Date(day + 'T00:00:00').toLocaleDateString('en-MY', { weekday: 'short', day: 'numeric', month: 'short' })}
                </p>
                <div className="space-y-1.5">
                  {grouped[day].map((e, idx) => {
                    const m = EVENT_META[e.event] || { label: e.event, color: '#6B7280', icon: Activity, bg: 'bg-[#6B7280]/10' };
                    const Icon = m.icon;
                    return (
                      <div key={idx} className={`flex items-center gap-2 py-1.5 px-2 rounded-md ${m.bg}`} data-testid={`mtproto-event-${idx}`}>
                        <Icon size={12} style={{ color: m.color }} />
                        <span className="text-xs font-medium" style={{ color: m.color }}>{m.label}</span>
                        <span className="text-[10px] text-[#9CA3AF] ml-1">
                          {new Date(e.at).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {e.consecutive_failures > 0 && (
                          <Badge className="text-[9px] bg-[#E63946]/15 text-[#E63946] border-[#E63946]/30">
                            failure #{e.consecutive_failures}
                          </Badge>
                        )}
                        {e.after_failures > 0 && (
                          <Badge className="text-[9px] bg-[#3B82F6]/15 text-[#3B82F6] border-[#3B82F6]/30">
                            after {e.after_failures} failure{e.after_failures === 1 ? '' : 's'}
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
